/**
 * StudySync New Participant Flow Component
 * Complete enrollment flow: Study Code → Email/Phone → Verification → Fitbit → Personal Info
 */

import React, { useState } from "react";
import {
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Box,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
} from "@mui/material";
import {
  Science,
  CheckCircle,
  Email,
  Phone,
  FitnessCenter,
  ArrowForward,
  ArrowBack,
  Refresh,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { AuthService } from "../../services/authService";
import VerificationService from "../../services/verificationService";
import { FitbitService } from "../../services/fitbitService";
import LoadingSpinner from "../common/LoadingSpinner";
import ParticipantInfoCollection from "./ParticipantInfoCollection";
import { formatPhoneNumber } from "../../utils/textUtils";
import type { StudyVerificationResult } from "../../types";
import { analyticsService } from "../../services/analyticsService";
import ConsentStep, {
  fetchOnboardingConsentStatus,
  type OnboardingConsentState,
} from "../consent_management/components/participant/ConsentStep";

const baseSteps = [
  "Enter Study Code",
  "Enter Contact Info",
  "Verify Pending Status",
  "Choose Verification Method",
  "Enter Verification Code",
  "Fitbit Authorization",
  "Personal Information",
  "Complete",
];

const isDevMode =
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_ENVIRONMENT === "development" ||
  process.env.REACT_APP_DEV_MODE === "true" ||
  process.env.REACT_APP_DEV_MODE === "development";

interface PendingParticipant {
  participantId: string;
  email: string;
  phoneNumber?: string;
  studyCode: string;
  studyName: string;
  status: "pending" | "approved" | "rejected";
}

const NewParticipantFlow: React.FC = () => {
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Form data
  const [studyCode, setStudyCode] = useState("");
  const [email, setEmail] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [verificationMethod, setVerificationMethod] = useState<
    "email" | "phone"
  >("email");
  const [verificationCode, setVerificationCode] = useState("");
  const [studyInfo, setStudyInfo] = useState<StudyVerificationResult | null>(
    null
  );
  const [pendingParticipant, setPendingParticipant] =
    useState<PendingParticipant | null>(null);
  const [consentRequirement, setConsentRequirement] =
    useState<OnboardingConsentState | null>(null);
  const [, setVerificationSent] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownTime = 30;

  const hasConsentStep =
    consentRequirement?.mode === "optional" || consentRequirement?.mode === "required";
  const steps = hasConsentStep
    ? [...baseSteps.slice(0, 5), "Consent", ...baseSteps.slice(5)]
    : baseSteps;
  const consentStepIndex = 5;
  const fitbitStepIndex = hasConsentStep ? 6 : 5;
  const personalInfoStepIndex = hasConsentStep ? 7 : 6;

  // Handle resend cooldown
  React.useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  // Check if coming back from Fitbit callback (should go to step 6)
  React.useEffect(() => {
    if (location.state?.step) {
      setActiveStep(location.state.step);
      if (location.state.participantId) {
        setPendingParticipant(
          (prev) =>
            ({
              ...prev,
              participantId: location.state.participantId,
              studyCode: location.state.studyCode,
              email: location.state.email || prev?.email || "",
              phoneNumber:
                location.state.phoneNumber || prev?.phoneNumber || "",
            } as any)
        );
      }
    }
  }, [location.state?.step, location.state?.participantId, location.state?.studyCode, location.state?.email, location.state?.phoneNumber]);

  // Step 1: Enter Study Code
  const handleStudyCodeSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!studyCode.trim()) {
      setError("Please enter a study code");
      return;
    }

    try {
      setLoading(true);
      setError("");

      const response = await AuthService.verifyStudyCode(studyCode.trim());

      if (response.success && response.study) {
        setStudyInfo(response.study);
        setActiveStep(1);
        analyticsService.trackParticipantStudyCodeEntered({ success: true });
        setSuccess("Study found! Please enter your contact information.");
      } else {
        analyticsService.trackParticipantStudyCodeEntered({ success: false });
        setError(
          response.error || "Study not found. Please check the study code."
        );
      }
    } catch (err: any) {
      analyticsService.trackParticipantStudyCodeEntered({ success: false });
      setError("Failed to verify study: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

  // Step 2: Enter Contact Info
  const handleContactInfoSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim()) {
      setError("Please enter your email address");
      return;
    }

    try {
      setLoading(true);
      setError("");

      // Phone is optional - only send if entered
      const checkPendingData: any = {
        studyCode: studyCode.trim(),
        email: email.trim().toLowerCase(),
      };

      if (phoneNumber && phoneNumber.trim()) {
        checkPendingData.phoneNumber = phoneNumber.trim();
      }

      // Check if participant is pending in the study
      const response = await fetch(
        `${
          process.env.REACT_APP_API_URL || "http://localhost:3002"
        }/api/participants/check-pending`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(checkPendingData),
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        if (data.pending) {
          setPendingParticipant(data.participant);
          setActiveStep(3); // Skip to verification method selection

          // Determine which verification methods are available
          if (!data.participant.phoneNumber) {
            // No phone provided - can only use email
            setVerificationMethod("email");
          }

          setSuccess(
            "You are approved for this study! Please choose your verification method."
          );
        } else {
          setError(
            "You are not approved for this study. Please contact the researcher."
          );
        }
      } else {
        setError(data.message || "Failed to check pending status");
      }
    } catch (err: any) {
      setError(
        "Failed to check pending status: " + (err.message || "Unknown error")
      );
    } finally {
      setLoading(false);
    }
  };

  // Step 4: Send Verification Code
  const handleSendVerificationCode = async () => {
    if (!pendingParticipant) return;

    try {
      setLoading(true);
      setError("");

      if (verificationMethod === "email") {
        await VerificationService.sendEmailVerificationCode({
          participantId: pendingParticipant.participantId,
          email: pendingParticipant.email,
          studyCode: pendingParticipant.studyCode,
        });
      } else {
        await VerificationService.sendPhoneVerificationCode({
          participantId: pendingParticipant.participantId,
          phoneNumber: pendingParticipant.phoneNumber || "",
          studyCode: pendingParticipant.studyCode,
        });
      }

      setVerificationSent(true);
      setSuccess(`Verification code sent to your ${verificationMethod}!`);
      setResendCooldown(cooldownTime);
      setActiveStep(4);
    } catch (err: any) {
      setError(err.message || "Failed to send verification code");
    } finally {
      setLoading(false);
    }
  };

  // Handle resend verification code
  const handleResendCode = async () => {
    if (!pendingParticipant) return;

    try {
      setLoading(true);
      setError("");
      setSuccess("");

      if (verificationMethod === "email") {
        await VerificationService.sendEmailVerificationCode({
          participantId: pendingParticipant.participantId,
          email: pendingParticipant.email,
          studyCode: pendingParticipant.studyCode,
        });
      } else {
        await VerificationService.sendPhoneVerificationCode({
          participantId: pendingParticipant.participantId,
          phoneNumber: pendingParticipant.phoneNumber || "",
          studyCode: pendingParticipant.studyCode,
        });
      }

      setSuccess("Verification code sent!");
      setResendCooldown(cooldownTime);
    } catch (err: any) {
      setError(err.message || "Failed to send verification code");
    } finally {
      setLoading(false);
    }
  };

  const loadOnboardingConsentRequirement = async (participant: PendingParticipant) => {
    const next = await fetchOnboardingConsentStatus(
      participant.participantId,
      participant.studyCode
    );
    setConsentRequirement(next);
    return next;
  };

  // Step 5: Verify Code
  const handleVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!VerificationService.validateVerificationCode(verificationCode)) {
      setError("Please enter a valid 6-digit code");
      return;
    }

    if (!pendingParticipant) return;

    try {
      setLoading(true);
      setError("");

      const response =
        verificationMethod === "email"
          ? await VerificationService.verifyEmailCode({
              participantId: pendingParticipant.participantId,
              email: pendingParticipant.email,
              studyCode: pendingParticipant.studyCode,
              verificationCode,
            })
          : await VerificationService.verifyPhoneCode({
              participantId: pendingParticipant.participantId,
              phoneNumber: pendingParticipant.phoneNumber || "",
              studyCode: pendingParticipant.studyCode,
              verificationCode,
            });

      if (response.success) {
        const consentStatus = await loadOnboardingConsentRequirement(pendingParticipant);
        setSuccess(
          consentStatus.mode === "none"
            ? "Verification successful!"
            : "Verification successful! Please review the consent step before Fitbit authorization."
        );
        setActiveStep(consentStepIndex);
      }
    } catch (err: any) {
      setError(err.message || "Invalid verification code");
    } finally {
      setLoading(false);
    }
  };

  // Step 6: Continue to Fitbit authorization
  const handleDataSourceContinue = () => {
    analyticsService.trackParticipantDataSourceSelected({ dataSource: "fitbit" });
    handleProceedToFitbit();
  };

  const handleSkipFitbitForDevelopment = () => {
    if (!pendingParticipant || !isDevMode) return;

    localStorage.removeItem("fitbit_code_verifier");
    localStorage.removeItem("fitbit_study_code");
    localStorage.removeItem("fitbit_participant_data");
    sessionStorage.removeItem("studysync_participant_web_tokens");

    analyticsService.trackParticipantDataSourceSelected({ dataSource: "fitbit" });
    setError("");
    setSuccess(
      "Development mode: Fitbit authorization skipped. Continue with personal information."
    );
    setActiveStep(personalInfoStepIndex);
  };

  const handleConsentStepComplete = () => {
    setError("");
    setSuccess("Consent recorded. Continue with Fitbit authorization.");
    setActiveStep(fitbitStepIndex);
  };

  const handleConsentStepSkip = () => {
    setError("");
    setSuccess("Consent skipped. Continue with Fitbit authorization.");
    setActiveStep(fitbitStepIndex);
  };

  // Step 6: Proceed to Fitbit Authorization
  const handleProceedToFitbit = async () => {
    if (!pendingParticipant) return;

    try {
      setLoading(true);
      setError("");

      // Add a small delay to ensure smooth transition
      await new Promise((resolve) => setTimeout(resolve, 500));

      // Use the backend-generated Fitbit auth URL so the participant web flow
      // stays aligned with the backend callback/state handling.
      const authUrlResponse = await FitbitService.getAuthUrl(
        pendingParticipant.participantId,
        pendingParticipant.studyCode
      );

      if (!authUrlResponse.success || !authUrlResponse.authUrl) {
        throw new Error("Failed to generate Fitbit authorization URL");
      }

      // Store participant data for the callback
      localStorage.removeItem("fitbit_code_verifier");
      localStorage.removeItem("fitbit_study_code");
      localStorage.setItem(
        "fitbit_participant_data",
        JSON.stringify({
          participantId: pendingParticipant.participantId,
          email: pendingParticipant.email,
          studyCode: pendingParticipant.studyCode,
          studyName: pendingParticipant.studyName,
        })
      );

      // Add another small delay before redirect to ensure everything is ready
      await new Promise((resolve) => setTimeout(resolve, 300));

      // Redirect to Fitbit authorization
      window.location.href = authUrlResponse.authUrl;
    } catch (err: any) {
      setError(
        "Failed to initiate Fitbit authorization: " +
          (err.message || "Unknown error")
      );
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (activeStep > 0) {
      setActiveStep(activeStep - 1);
    }
  };

  const handleCodeChange = (value: string) => {
    const digitsOnly = value.replace(/\D/g, "").slice(0, 6);
    setVerificationCode(digitsOnly);
  };

  if (loading && activeStep === 0) {
    return <LoadingSpinner message="Verifying study code..." />;
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: "center", mb: 4 }}>
          <Science sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
          <Typography
            variant="h4"
            component="h1"
            sx={{ fontWeight: 600, mb: 1 }}
          >
            Join Research Study
          </Typography>
          <Typography variant="h6" color="text.secondary">
            Web enrollment for Fitbit participants
          </Typography>
        </Box>

        {/* Progress Stepper */}
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label} completed={index < activeStep}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert
            severity="success"
            sx={{ mb: 3 }}
            onClose={() => setSuccess("")}
          >
            {success}
          </Alert>
        )}

        {/* Step Content */}
        <Paper elevation={3} sx={{ p: 4 }}>
          {/* Step 0: Enter Study Code */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Enter Study Code
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Please enter the study code provided by the researcher. This web
                flow is for Fitbit enrollment. Apple Health (HealthKit)
                participants should enroll in the StudySync iOS app using the
                HealthKit enrollment QR code.
              </Typography>

              <Box component="form" onSubmit={handleStudyCodeSubmit}>
                <TextField
                  fullWidth
                  label="Study Code"
                  value={studyCode}
                  onChange={(e) => setStudyCode(e.target.value.toUpperCase())}
                  placeholder="e.g., ABC123"
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <Science sx={{ mr: 1, color: "text.secondary" }} />
                    ),
                  }}
                />

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading}
                  endIcon={<ArrowForward />}
                >
                  {loading ? "Verifying..." : "Verify Study Code"}
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 1: Enter Contact Info */}
          {activeStep === 1 && studyInfo && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Enter Your Contact Information
              </Typography>

              <Card sx={{ mb: 3, backgroundColor: "grey.50" }}>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Study Information
                  </Typography>
                  <Typography variant="h5" sx={{ mb: 1, fontWeight: 600 }}>
                    {studyInfo.studyName}
                  </Typography>
                  {studyInfo.studyDescription && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {studyInfo.studyDescription}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    <strong>Study Code:</strong> {studyInfo.studyCode}
                  </Typography>
                  {studyInfo.researcherName && (
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      <strong>Researcher:</strong> {studyInfo.researcherName}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    Research Study
                  </Typography>
                </CardContent>
              </Card>

              <Box component="form" onSubmit={handleContactInfoSubmit}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <Email sx={{ mr: 1, color: "text.secondary" }} />
                    ),
                  }}
                />

                <TextField
                  fullWidth
                  label="Phone Number (Optional)"
                  value={phoneNumber}
                  onChange={(e) =>
                    setPhoneNumber(formatPhoneNumber(e.target.value))
                  }
                  placeholder="(000)-0000-000"
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <Phone sx={{ mr: 1, color: "text.secondary" }} />
                    ),
                  }}
                />

                <Box sx={{ display: "flex", gap: 2 }}>
                  <Button
                    variant="outlined"
                    onClick={handleBack}
                    startIcon={<ArrowBack />}
                  >
                    Back
                  </Button>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    endIcon={<ArrowForward />}
                    sx={{ flex: 1 }}
                  >
                    {loading ? "Checking..." : "Check Pending Status"}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* Step 3: Choose Verification Method */}
          {activeStep === 3 && pendingParticipant && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Choose Verification Method
              </Typography>

              <Card sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <strong>Participant ID:</strong>{" "}
                    {pendingParticipant.participantId}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <strong>Email:</strong> {pendingParticipant.email}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    <strong>Phone:</strong>{" "}
                    {pendingParticipant.phoneNumber || "Not provided"}
                  </Typography>
                </CardContent>
              </Card>

              <FormControl component="fieldset" sx={{ mb: 3 }}>
                <FormLabel component="legend">
                  {pendingParticipant.phoneNumber
                    ? "How would you like to receive your verification code?"
                    : "Verification method"}
                </FormLabel>
                <RadioGroup
                  value={verificationMethod}
                  onChange={(e) =>
                    setVerificationMethod(e.target.value as "email" | "phone")
                  }
                >
                  <FormControlLabel
                    value="email"
                    control={<Radio />}
                    label={
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Email sx={{ mr: 1 }} />
                        Email ({pendingParticipant.email})
                      </Box>
                    }
                  />
                  {pendingParticipant.phoneNumber && (
                    <FormControlLabel
                      value="phone"
                      control={<Radio />}
                      label={
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          <Phone sx={{ mr: 1 }} />
                          Phone ({pendingParticipant.phoneNumber})
                        </Box>
                      }
                    />
                  )}
                  {!pendingParticipant.phoneNumber && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, ml: 4 }}
                    >
                      📧 Phone verification is not available for your account.
                      Email will be used.
                    </Typography>
                  )}
                </RadioGroup>
              </FormControl>

              <Box sx={{ display: "flex", gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  startIcon={<ArrowBack />}
                >
                  Back
                </Button>
                <Button
                  variant="contained"
                  onClick={handleSendVerificationCode}
                  disabled={loading}
                  endIcon={<ArrowForward />}
                  sx={{ flex: 1 }}
                >
                  {loading ? "Sending..." : "Send Verification Code"}
                </Button>
              </Box>
            </Box>
          )}

          {/* Step 4: Enter Verification Code */}
          {activeStep === 4 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 3 }}>
                Enter Verification Code
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter the 6-digit code sent to your {verificationMethod}:
              </Typography>

              <Box component="form" onSubmit={handleVerifyCode}>
                <TextField
                  fullWidth
                  label="Verification Code"
                  value={verificationCode}
                  onChange={(e) => handleCodeChange(e.target.value)}
                  placeholder="123456"
                  inputProps={{
                    maxLength: 6,
                    style: {
                      textAlign: "center",
                      fontSize: "1.5rem",
                      letterSpacing: "0.5em",
                    },
                  }}
                  sx={{ mb: 3 }}
                  disabled={loading}
                />

                <Box sx={{ display: "flex", gap: 2, mb: 3 }}>
                  <Button
                    variant="contained"
                    type="submit"
                    disabled={loading || verificationCode.length !== 6}
                    endIcon={<CheckCircle />}
                    sx={{ flex: 1 }}
                  >
                    {loading ? "Verifying..." : "Verify Code"}
                  </Button>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    mb: 3,
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Didn't receive the code?
                  </Typography>
                  <Button
                    variant="text"
                    onClick={handleResendCode}
                    disabled={loading || resendCooldown > 0}
                    startIcon={<Refresh />}
                    size="small"
                  >
                    {resendCooldown > 0
                      ? `Resend in ${resendCooldown}s`
                      : "Resend Code"}
                  </Button>
                </Box>

                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setVerificationSent(false);
                      setVerificationCode("");
                      setActiveStep(3);
                    }}
                    startIcon={<ArrowBack />}
                  >
                    Back to Contact Info
                  </Button>
                </Box>
              </Box>
            </Box>
          )}

          {/* Step 5: Consent */}
          {activeStep === consentStepIndex && hasConsentStep && pendingParticipant && (
            <ConsentStep
              participantId={pendingParticipant.participantId}
              studyCode={pendingParticipant.studyCode}
              participantEmail={pendingParticipant.email}
              participantPhone={pendingParticipant.phoneNumber}
              initialStatus={consentRequirement}
              onStatusChange={setConsentRequirement}
              onContinue={handleConsentStepComplete}
              onSkip={handleConsentStepSkip}
              onBack={handleBack}
            />
          )}

          {/* Step 5/6: Fitbit Authorization */}
          {activeStep === fitbitStepIndex && !hasConsentStep && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Connect Fitbit
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Connect your Fitbit account to share your health data with this
                study.
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                <Card
                  sx={{
                    width: 220,
                    border: "2px solid",
                    borderColor: "primary.main",
                    boxShadow: 3,
                    transform: "scale(1.03)",
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                      p: 3,
                    }}
                  >
                    <FitnessCenter sx={{ fontSize: 48, color: "#00B0B9" }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Fitbit
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 4,
                  }}
                >
                  <LoadingSpinner message="Preparing authorization..." />
                </Box>
              ) : (
                <>
                  {isDevMode && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      Development mode: you can skip Fitbit authorization and
                      complete enrollment without device tokens.
                    </Alert>
                  )}
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleBack}
                      startIcon={<ArrowBack />}
                    >
                      Back
                    </Button>
                    {isDevMode && (
                      <Button
                        variant="outlined"
                        onClick={handleSkipFitbitForDevelopment}
                      >
                        Skip Fitbit
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={handleDataSourceContinue}
                      endIcon={<ArrowForward />}
                      size="large"
                      disabled={loading}
                    >
                      Continue
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}

          {activeStep === fitbitStepIndex && hasConsentStep && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>
                Connect Fitbit
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Connect your Fitbit account to share your health data with this
                study.
              </Typography>

              <Box sx={{ display: "flex", justifyContent: "center", mb: 4 }}>
                <Card
                  sx={{
                    width: 220,
                    border: "2px solid",
                    borderColor: "primary.main",
                    boxShadow: 3,
                    transform: "scale(1.03)",
                    transition: "all 0.2s ease-in-out",
                  }}
                >
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      gap: 1,
                      p: 3,
                    }}
                  >
                    <FitnessCenter sx={{ fontSize: 48, color: "#00B0B9" }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Fitbit
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 4,
                  }}
                >
                  <LoadingSpinner message="Preparing authorization..." />
                </Box>
              ) : (
                <>
                  {isDevMode && (
                    <Alert severity="info" sx={{ mb: 3 }}>
                      Development mode: you can skip Fitbit authorization and
                      complete enrollment without device tokens.
                    </Alert>
                  )}
                  <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={handleBack}
                      startIcon={<ArrowBack />}
                    >
                      Back
                    </Button>
                    {isDevMode && (
                      <Button
                        variant="outlined"
                        onClick={handleSkipFitbitForDevelopment}
                      >
                        Skip Fitbit
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={handleDataSourceContinue}
                      endIcon={<ArrowForward />}
                      size="large"
                      disabled={loading}
                    >
                      Continue
                    </Button>
                  </Box>
                </>
              )}
            </Box>
          )}

          {/* Step 6/7: Personal Information */}
          {activeStep === personalInfoStepIndex && pendingParticipant && (
            <Box>
              <ParticipantInfoCollection
                participantId={pendingParticipant.participantId}
                studyCode={pendingParticipant.studyCode}
                email={pendingParticipant.email}
                phoneNumber={pendingParticipant.phoneNumber}
              />
            </Box>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default NewParticipantFlow;
